import { useEffect, useRef } from 'react'
import { MdClose, MdHelpOutline } from 'react-icons/md'

export type FinancialHelpTopic =
    | 'sales'
    | 'receivable'
    | 'payable'
    | 'revenue'
    | 'cost'
    | 'expense'
    | 'profit'
    | 'overdue'
    | 'status'

interface HelpSection {
    id: FinancialHelpTopic
    title: string
    what: string
    date: string
    why?: string
}

/**
 * O que cada número do dashboard soma, por qual data, e por que ele difere do vizinho.
 *
 * Existe porque os números parecem dizer a mesma coisa e não dizem: "Pago" na grade
 * de status era lido como "recebido" (junta o que se pagou a parceiros), e "Receita"
 * não bate com o "Recebido" da tabela de vendas (uma conta pelo dia em que o dinheiro
 * entrou, a outra pelo dia da venda). A explicação mora na tela, não na cabeça de quem
 * montou o dashboard.
 */
const SECTIONS: HelpSection[] = [
    {
        id: 'sales',
        title: 'O que foi vendido',
        what:
            'Consultas, exames e procedimentos vendidos no período, e quanto deles já entrou. Orçamento e cancelado não contam. O vendido é líquido da taxa do gateway — o mesmo número do bloco "Por tipo de atendimento". Convênio conta como venda (o paciente não paga, o convênio paga na fatura) e só aparece como recebido quando a fatura for paga.',
        date:
            'Data da venda: a aprovação do orçamento, ou o lançamento quando o atendimento já nasceu agendado. O recebido não tem corte de data: é tudo o que as cobranças desses atendimentos trouxeram, até o fim do período e até hoje.',
        why:
            'Um procedimento flex de R$ 23.000 que recebeu R$ 3.000 de entrada aparece como vendido R$ 23.000 e recebido R$ 3.000. O que falta fica à vista em "A receber".',
    },
    {
        id: 'receivable',
        title: 'A Receber',
        what:
            'O que AINDA FALTA receber das contas a receber com vencimento no período: pendentes, vencidas e pagas em parte (valor menos o que já entrou). O já recebido não entra. Abaixo do valor aparece o total lançado com vencimento no período.',
        date: 'Data de vencimento.',
        why:
            'Para saber quanto ainda vai entrar das contas do período. Quanto já entrou está em "Receita"; o que venceu e não foi pago, de qualquer mês, está em "Vencidos".',
    },
    {
        id: 'payable',
        title: 'A Pagar',
        what:
            'O que AINDA FALTA pagar das contas a pagar com vencimento no período (repasses a parceiros, profissionais e hospitais, fornecedores e despesas). Lotes de repasse não entram: as contas agrupadas continuam valendo, e somar o lote contaria o mesmo dinheiro duas vezes.',
        date: 'Data de vencimento.',
    },
    {
        id: 'revenue',
        title: 'Receita',
        what:
            'Dinheiro que ENTROU no período: o valor pago das contas a receber quitadas. Inclui pagamentos de vendas de meses anteriores (parcelas, flex) e os recebimentos avulsos do Asaas, que não têm atendimento ligado.',
        date: 'Data do pagamento.',
        why:
            'É por isso que a Receita é maior que o "Recebido até…" da tabela de vendas: a tabela só conta o que entrou das vendas DO período; a Receita conta todo dinheiro que entrou no período, de qualquer venda.',
    },
    {
        id: 'cost',
        title: 'Custo',
        what:
            'Quanto a receita do período custou: o repasse congelado na venda (parceiro, profissional, hospital) e os insumos, das contas a receber pagas no período. Não é o que foi pago a parceiros no período — esse é o caixa de repasses.',
        date: 'Data do pagamento da conta a receber.',
        why:
            'No pagamento em partes e no flex, o repasse inteiro fica na cobrança que fecha o total. Em "sem custo apurado" estão cobranças com parceiro e sem valor de repasse: entram na receita com custo zero e inflam o lucro.',
    },
    {
        id: 'expense',
        title: 'Despesa',
        what:
            'Gasto da operação que não pertence a venda nenhuma: aluguel, energia, material, folha. Repasse não entra aqui (já está no Custo), senão o mesmo dinheiro contaria duas vezes. Valor baixo costuma significar despesa ainda não lançada no sistema.',
        date: 'Data do pagamento.',
    },
    {
        id: 'profit',
        title: 'Lucro',
        what: 'Receita − Custo − Despesa, todos do mesmo período.',
        date: 'Data do pagamento (a mesma dos três).',
    },
    {
        id: 'overdue',
        title: 'Vencidos',
        what:
            'Contas em aberto com vencimento anterior a hoje, de QUALQUER data — não só do período. Segue o seletor A Receber / A Pagar; em "Todos", mostra os vencidos a receber, e os a pagar aparecem abaixo do valor.',
        date: 'Vencimento antes de hoje, sem limite de período.',
        why:
            'Vencidos a pagar inclui repasses antigos nunca baixados (muitos do sistema anterior). Esse número só fica confiável depois que eles forem conferidos.',
    },
    {
        id: 'status',
        title: 'Lançamentos por status',
        what:
            'Contas a receber e a pagar com vencimento no período, agrupadas pelo status atual. Cada card separa as contas de clientes das contas de parceiros e fornecedores.',
        date: 'Data de vencimento.',
        why:
            '"Pago" aqui NÃO é o que entrou: junta o que se pagou a parceiros e conta pelo vencimento, não pelo dia do pagamento. O que entrou no período é a "Receita".',
    },
]

interface FinancialHelpModalProps {
    /** Tópico em destaque; `null` = modal fechado. */
    topic: FinancialHelpTopic | null
    onClose: () => void
}

export default function FinancialHelpModal({ topic, onClose }: FinancialHelpModalProps) {
    const refs = useRef<Partial<Record<FinancialHelpTopic, HTMLElement | null>>>({})

    useEffect(() => {
        if (topic) refs.current[topic]?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }, [topic])

    useEffect(() => {
        if (!topic) return
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [topic, onClose])

    if (!topic) return null

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2">
                        <MdHelpOutline className="text-xl text-secondary" />
                        <div>
                            <h3 className="text-lg font-semibold text-base-content">
                                Como ler o dashboard
                            </h3>
                            <p className="text-sm text-base-content/60">
                                O que cada número soma, por qual data e por quê.
                            </p>
                        </div>
                    </div>
                    <button
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <MdClose className="text-lg" />
                    </button>
                </div>

                <div className="flex flex-col gap-3 max-h-[65vh] overflow-y-auto pr-1">
                    {SECTIONS.map(section => {
                        const active = section.id === topic
                        return (
                            <section
                                key={section.id}
                                ref={el => {
                                    refs.current[section.id] = el
                                }}
                                className={`rounded-xl border p-4 flex flex-col gap-2 ${
                                    active
                                        ? 'border-secondary/60 bg-secondary/5'
                                        : 'border-base-300/60'
                                }`}
                            >
                                <h4 className="font-semibold text-base-content">{section.title}</h4>
                                <p className="text-sm text-base-content/80">{section.what}</p>
                                <p className="text-xs text-base-content/60">
                                    <span className="font-medium text-base-content/70">Data: </span>
                                    {section.date}
                                </p>
                                {section.why && (
                                    <p className="text-xs text-base-content/60">
                                        <span className="font-medium text-base-content/70">Por quê: </span>
                                        {section.why}
                                    </p>
                                )}
                            </section>
                        )
                    })}
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
